import React from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Divider, FormControl, MenuItem, Select, Skeleton, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import colors from "assets/theme-dark/base/colors";
import ColumnTable from "./column-preview";
import { TableOptions } from "./TableOptions";
import { useMainView } from "../services/useMainView";
import Pagination from "components/Pagination";
import CloseIcon from "@mui/icons-material/Close";
import DeleteMergedViewButton from "../../upload/components/DeleteMergedViewButton";
import { styled } from "@mui/system";
import pxToRem from "assets/theme/functions/pxToRem";
import ConfirmationDialog from "../../upload/components/ConfirmationDialogComponent";
import { useRemoveMatcherViews } from "../services/useRemoveMatcherViews";

interface TableItem {
  name: string;
  [key: string]: unknown;
}

const StyledCard = styled(MDBox)`
  width: 100%;
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
  overflow: hidden;
`;

const TruncatedText = styled(MDTypography)({
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "block",
});

export function MainView(): JSX.Element {
  const {
    handleSelect,
    isTableDisabled,
    matcherData,
    selectedTables,
    areTablesSelected,
    matcherTableData,
    selectedMatcherColumn,
    identifier,
    matcherTableHeadersData,
    setIdentifier,
    columnPairs,
    addColumnPair,
    handleColumnChange,
    handleMatchTables,
    isColumnSelected,
    removeColumnPair,
    viewHeadersData,
    selectedColumn,
    viewData,
    views,
    matcherPageData,
    handleMatcherPageChange,
    handleMatcherPageSizeChange,
    viewPageData,
    handleViewPageChange,
    handleViewPageSizeChange,
    currentMatcherTables,
    currentViewsTables,
    selectedCube,
  } = useMainView();

  const { removeMatcherViewLoading, dialogOpen, handleOpenDialog, handleCloseDialog, handleConfirmDelete } =
    useRemoveMatcherViews();

  if (!views || !matcherData) {
    return <></>;
  }

  return (
    <MDBox sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <StyledCard>
        <MDBox display="flex" justifyContent="space-between">
          <MDBox sx={{ maxWidth: "45%", flex: 1 }}>
            <MDTypography variant="h4" sx={{ color: colors.text.main, mb: 1 }}>
              Choose a table to match the data to
            </MDTypography>
            <MDBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {(currentMatcherTables as unknown as TableItem[]).map((table) => (
                <MDBox
                  key={table.name}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: pxToRem(150),
                    height: pxToRem(75),
                    borderRadius: pxToRem(12),
                    userSelect: "none",
                    background:
                      selectedTables.row1 === table.name
                        ? "linear-gradient(135deg, #0d00ff 0%, #b3c6ff 100%)"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(245, 245, 245, 0.7) 100%)",
                    backdropFilter: "blur(8px)",
                    boxShadow:
                      selectedTables.row1 === table.name
                        ? "0 8px 24px rgba(13, 0, 255, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2)"
                        : "0 6px 20px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.3)",
                    border:
                      selectedTables.row1 === table.name
                        ? "1px solid rgba(255, 255, 255, 0.5)"
                        : "1px solid rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    cursor: isTableDisabled(table.name, "row1") ? "not-allowed" : "pointer",
                    opacity: isTableDisabled(table.name, "row1") ? 0.5 : 1,
                    transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out, background 0.3s ease-in-out",
                    "&:hover": {
                      transform: isTableDisabled(table.name, "row1") ? "none" : "scale(1.05) translateY(-4px)",
                      boxShadow: isTableDisabled(table.name, "row1")
                        ? undefined
                        : "0 12px 32px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.15)",
                      background: isTableDisabled(table.name, "row1")
                        ? undefined
                        : selectedTables.row1 === table.name
                        ? "linear-gradient(135deg, #3b33ff 0%, #b3c6ff 100%)"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 250, 250, 0.9) 100%)",
                    },
                    "&:active": {
                      transform: isTableDisabled(table.name, "row1") ? "none" : "scale(0.98)",
                      boxShadow: isTableDisabled(table.name, "row1") ? undefined : "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                  onClick={() => !isTableDisabled(table.name, "row1") && handleSelect(table.name, "row1")}
                >
                  {table.name.length > 10 ? (
                    <Tooltip title={table.name} arrow>
                      <TruncatedText
                        variant="button"
                        fontWeight="medium"
                        color={selectedTables.row1 === table.name ? "white" : "secondary.main"}
                        sx={{
                          fontSize: pxToRem(14),
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          textShadow: selectedTables.row1 === table.name ? "0 2px 4px rgba(0, 0, 0, 0.3)" : "none",
                          px: 1,
                        }}
                      >
                        {table.name}
                      </TruncatedText>
                    </Tooltip>
                  ) : (
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      color={selectedTables.row1 === table.name ? "white" : "secondary.main"}
                      sx={{
                        fontSize: pxToRem(14),
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {table.name}
                    </MDTypography>
                  )}
                  {selectedTables.row1 === table.name && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: -3,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Delete View">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleOpenDialog(table.name)}
                          disabled={removeMatcherViewLoading}
                          aria-label="delete view"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <ConfirmationDialog
                        open={dialogOpen === table.name}
                        onCancel={handleCloseDialog}
                        onConfirm={() => handleConfirmDelete(table.name)}
                        title={table.name.length > 30 ? `${table.name.slice(0, 30)}...` : table.name}
                        message={`Are you sure you want to delete the view ${table.name}?`}
                      />
                    </MDBox>
                  )}
                </MDBox>
              ))}
            </MDBox>
            <MDBox sx={{ marginRight: "1.2rem", mt: 2, display: "flex", justifyContent: "start" }}>
              <Pagination
                pageData={matcherPageData}
                changePage={handleMatcherPageChange}
                changePageSize={handleMatcherPageSizeChange}
                rowsPerPageOptions={[4, 8, 12]}
              />
            </MDBox>
          </MDBox>

          <MDBox sx={{ alignSelf: "stretch" }}>
            <Divider orientation="vertical" />
          </MDBox>

          <MDBox sx={{ maxWidth: "45%", flex: 1 }}>
            <MDTypography variant="h4" sx={{ color: colors.text.main, mb: 1 }}>
              Choose another table to match with
            </MDTypography>
            <MDBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {(currentViewsTables as unknown as TableItem[]).map((table) => (
                <MDBox
                  key={table.name}
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    width: pxToRem(150),
                    height: pxToRem(75),
                    borderRadius: pxToRem(12),
                    userSelect: "none",
                    background:
                      selectedTables.row2 === table.name
                        ? "linear-gradient(135deg, #0d00ff 0%, #b3c6ff 100%)"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(245, 245, 245, 0.7) 100%)",
                    backdropFilter: "blur(8px)",
                    boxShadow:
                      selectedTables.row2 === table.name
                        ? "0 8px 24px rgba(13, 0, 255, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2)"
                        : "0 6px 20px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.3)",
                    border:
                      selectedTables.row2 === table.name
                        ? "1px solid rgba(255, 255, 255, 0.5)"
                        : "1px solid rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    cursor: isTableDisabled(table.name, "row2") ? "not-allowed" : "pointer",
                    opacity: isTableDisabled(table.name, "row2") ? 0.5 : 1,
                    transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out, background 0.3s ease-in-out",
                    "&:hover": {
                      transform: isTableDisabled(table.name, "row2") ? "none" : "scale(1.05) translateY(-4px)",
                      boxShadow: isTableDisabled(table.name, "row2")
                        ? undefined
                        : "0 12px 32px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.15)",
                      background: isTableDisabled(table.name, "row2")
                        ? undefined
                        : selectedTables.row2 === table.name
                        ? "linear-gradient(135deg, #3b33ff 0%, #b3c6ff 100%)"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 250, 250, 0.9) 100%)",
                    },
                    "&:active": {
                      transform: isTableDisabled(table.name, "row2") ? "none" : "scale(0.98)",
                      boxShadow: isTableDisabled(table.name, "row2") ? undefined : "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                  onClick={() => !isTableDisabled(table.name, "row2") && handleSelect(table.name, "row2")}
                >
                  {table.name.length > 10 ? (
                    <Tooltip title={table.name} arrow>
                      <TruncatedText
                        variant="button"
                        fontWeight="medium"
                        color={selectedTables.row2 === table.name ? "white" : "secondary.main"}
                        sx={{
                          fontSize: pxToRem(14),
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          px: 1,
                        }}
                      >
                        {table.name}
                      </TruncatedText>
                    </Tooltip>
                  ) : (
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      color={selectedTables.row2 === table.name ? "white" : "secondary.main"}
                      sx={{
                        fontSize: pxToRem(14),
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {table.name}
                    </MDTypography>
                  )}
                  {selectedTables.row2 === table.name && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: -3,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DeleteMergedViewButton
                        cubeId={selectedCube.cubeId}
                        viewName={table.name}
                        disableDeleteButton={false}
                      />
                    </MDBox>
                  )}
                </MDBox>
              ))}
            </MDBox>
            <MDBox sx={{ marginRight: "1.2rem", mt: 2 }}>
              <Pagination
                pageData={viewPageData}
                changePage={handleViewPageChange}
                changePageSize={handleViewPageSizeChange}
                rowsPerPageOptions={[4, 8, 12]}
              />
            </MDBox>
          </MDBox>
        </MDBox>
      </StyledCard>

      {areTablesSelected && (
        <StyledCard>
          <MDTypography variant="h4" sx={{ color: colors.text.main, mb: 0.5 }}>
            Choose identifiers
          </MDTypography>
          <MDBox display="flex" gap={4} sx={{ marginTop: "2rem", width: "100%", overflow: "hidden" }}>
            <MDBox flex={1} sx={{ flex: "0 0 25%", overflow: "hidden" }}>
              {selectedMatcherColumn ? (
                <ColumnTable
                  column={selectedMatcherColumn}
                  values={matcherTableData?.matcherTableData?.edges?.map((edge) => edge[selectedMatcherColumn])}
                />
              ) : (
                <MDTypography sx={{ color: colors.grey[500] }}>
                  A peek into the selected column&apos;s data
                </MDTypography>
              )}
            </MDBox>
            <MDBox display="flex" flexDirection="column" sx={{ flex: "0 0 45%", overflow: "hidden" }}>
              <MDTypography sx={{ color: colors.grey[800] }}>
                Select the column that will be matched to the data
              </MDTypography>
              <MDBox display="flex" marginTop="1rem">
                <TableOptions
                  identifier={identifier}
                  matcherTableHeadersData={matcherTableHeadersData}
                  onIdentifierSelect={setIdentifier}
                />
              </MDBox>
              <MDBox display="flex" marginTop="1.2rem" flexDirection="column" gap={2}>
                <MDTypography sx={{ color: colors.grey[800] }}>
                  Select the column pairs that will be matched
                </MDTypography>
                {columnPairs?.map((pair, index) => (
                  <MDBox key={index} display="flex" gap={2} alignItems="center">
                    <FormControl fullWidth sx={{ minWidth: 120 }}>
                      <Select
                        value={pair.columnA}
                        onChange={(e) => handleColumnChange(index, "columnA", e.target.value)}
                        displayEmpty
                        sx={{
                          backgroundColor: colors.secondary.main,
                          borderRadius: 0,
                          "& .MuiSelect-select": {
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "white",
                          },
                        }}
                        size="small"
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                              maxWidth: 400,
                              "& .MuiMenuItem-root": {
                                color: colors.text.main,
                                "&:hover": {
                                  backgroundColor: colors.grey[700],
                                  color: "white",
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled sx={{ color: colors.grey[500] }}>
                          Select Column A
                        </MenuItem>
                        {matcherTableHeadersData ? (
                          matcherTableHeadersData.matcherTableHeaders
                            .filter((column) => column !== identifier && column !== "FX_UNIQUE_ID")
                            .map((column) => (
                              <MenuItem key={column} value={column}>
                                {column.length >= 30 ? (
                                  <Tooltip title={column} arrow placement="right">
                                    <span
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "block",
                                      }}
                                    >
                                      {column}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <span>{column}</span>
                                )}
                              </MenuItem>
                            ))
                        ) : (
                          <MDBox display="grid" gap={1}>
                            <Skeleton
                              variant="rectangular"
                              height={32}
                              style={{
                                marginTop: "1rem",
                                backgroundColor: "rgba(255, 255, 255, 0.35)",
                                borderRadius: "5px",
                              }}
                            />
                            <Skeleton
                              variant="rectangular"
                              height={32}
                              style={{ backgroundColor: "rgba(255, 255, 255, 0.35)", borderRadius: "5px" }}
                            />
                            <Skeleton
                              variant="rectangular"
                              height={32}
                              style={{ backgroundColor: "rgba(255, 255, 255, 0.35)", borderRadius: "5px" }}
                            />
                          </MDBox>
                        )}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ minWidth: 120 }}>
                      <Select
                        value={pair.columnB}
                        onChange={(e) => handleColumnChange(index, "columnB", e.target.value)}
                        displayEmpty
                        sx={{
                          backgroundColor: colors.secondary.main,
                          borderRadius: 0,
                          "& .MuiSelect-select": {
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "white",
                          },
                        }}
                        size="small"
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                              maxWidth: 400,
                              "& .MuiMenuItem-root": {
                                color: colors.text.main,
                                "&:hover": {
                                  backgroundColor: colors.grey[700],
                                  color: "white",
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled sx={{ color: colors.grey[500] }}>
                          Select Column B
                        </MenuItem>
                        {viewHeadersData ? (
                          viewHeadersData.viewHeaders
                            .filter(
                              (column) =>
                                column !== identifier &&
                                column !== "CUBEID" &&
                                column !== "FX_UNIQUE_ID" &&
                                column !== "SOURCEFILEID" &&
                                column !== "DATASETID" &&
                                !column.includes("ismatchfinalized".toLowerCase()) &&
                                !column.startsWith("SCORE_")
                            )
                            .map((column) => (
                              <MenuItem key={column} value={column}>
                                {column.length >= 30 ? (
                                  <Tooltip title={column} arrow placement="right">
                                    <span
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "block",
                                      }}
                                    >
                                      {column}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <span>{column}</span>
                                )}
                              </MenuItem>
                            ))
                        ) : (
                          <MDBox display="grid" gap={1}>
                            <Skeleton
                              variant="rectangular"
                              height={32}
                              style={{
                                marginTop: "1rem",
                                backgroundColor: "rgba(255, 255, 255, 0.35)",
                                borderRadius: "5px",
                              }}
                            />
                            <Skeleton
                              variant="rectangular"
                              height={32}
                              style={{ backgroundColor: "rgba(255, 255, 255, 0.35)", borderRadius: "5px" }}
                            />
                            <Skeleton
                              variant="rectangular"
                              height={32}
                              style={{ backgroundColor: "rgba(255, 255, 255, 0.35)", borderRadius: "5px" }}
                            />
                          </MDBox>
                        )}
                      </Select>
                    </FormControl>
                    <MDButton
                      variant="contained"
                      color="error"
                      onClick={() => removeColumnPair(index)}
                      sx={{
                        borderRadius: 0,
                        minWidth: "40px",
                        padding: "8px",
                        "&:hover": { backgroundColor: colors.error.dark },
                      }}
                      disableElevation
                    >
                      <CloseIcon />
                    </MDButton>
                  </MDBox>
                ))}
                <MDButton
                  variant="contained"
                  color="secondary"
                  onClick={addColumnPair}
                  sx={{
                    justifyContent: "center",
                    maxWidth: "200px",
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: colors.secondary.focus },
                    "&:disabled": { backgroundColor: colors.grey[700], color: colors.grey[500] },
                    mt: 1,
                  }}
                >
                  <MDTypography variant="button" fontWeight="medium" color="white">
                    + Add Column Pair
                  </MDTypography>
                </MDButton>
                <MDBox display="flex" justifyContent="center" marginTop="1rem">
                  <MDButton
                    variant="background"
                    size="large"
                    disabled={
                      !isColumnSelected || columnPairs.some((pair) => !pair.columnA || !pair.columnB) || !identifier
                    }
                    sx={{
                      borderRadius: "8px",
                    }}
                    onClick={() => handleMatchTables()}
                  >
                    <MDTypography variant="button" fontWeight="medium" color="white">
                      Match Tables
                    </MDTypography>
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
            <MDBox flex={1} sx={{ flex: "0 0 25%", overflow: "hidden" }}>
              {selectedColumn ? (
                <ColumnTable
                  column={selectedColumn}
                  values={viewData?.viewData?.edges?.map((edge) => edge[selectedColumn])}
                />
              ) : (
                <MDTypography sx={{ color: colors.grey[500] }}>
                  A peek into the selected column&apos;s data
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </StyledCard>
      )}
    </MDBox>
  );
}
